<template>
  <v-container id="data-tables" tag="section" fluid>
    <base-material-card color="primary" icon="mdi-bulldozer" inline class="ml-0 px-5 py-4">
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">BulldozeRR</div>
      </template>
      <v-btn
        color="success"
        dark
        elevation="1"
        class="mt-5"
        style="float: left"
        relative
        text
        medium
        @click="$router.push(`add`)"
      >
        <v-icon left size="30px">mdi-plus-circle</v-icon>
        NOVO CÁLCULO
      </v-btn>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        class="break-search ml-auto mt-4"
        label="Procurar"
        hide-details
        single-line
        style="max-width: 265px"
      />

      <general-progress-bars class="mt-11" :items="downloadProgressData" prefix-text="Baixando Relatório" />
      <v-divider class="mt-10" />
      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        :loading="loading"
        :mobile-breakpoint="800"
        no-data-text="Nenhum cálculo bulldozerr encontrado"
      >
        <template v-slot:[`item.id`]="{ item }">
          {{ item.id }}
        </template>
        <template v-slot:[`item.nome`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="text-truncate-column" v-bind="attrs" v-on="on">
                {{ item.nome }}
              </div>
            </template>
            <span>{{ item.nome }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.usuario`]="{ item }">
          {{ item.usuario.nome }}
        </template>
        <template v-slot:[`item.data_processamento`]="{ item }">
          {{ item.data_processamento | parseToDateTimeBR }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <general-status-step
            title="Etapa"
            :items="[
              {
                nome: item.status_etapa_1,
                tipo: 'AIS'
              },
              {
                nome: item.status_etapa_2,
                tipo: 'BDGD'
              },
              {
                nome: item.status_etapa_3,
                tipo: 'ICMS'
              },
              {
                nome: item.status_etapa_4,
                tipo: 'Banco de Compras'
              },
              {
                nome: item.status_etapa_5,
                tipo: 'Base Incremental'
              },
              {
                nome: item.status_etapa_6,
                tipo: 'Obrigações Especiais'
              },
              {
                nome: item.status_etapa_7,
                tipo: 'Base Blindada'
              }
            ]"
          />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            min-width="0"
            :disabled="!canDownload(item)"
            class="px-1"
            fab
            icon
            x-small
            :loading="false"
            @click="openDialogDownload(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="white" class="ml-1" height="22px" width="22px" fab x-small elevation="1" v-bind="attrs" v-on="on">
                <v-icon small>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canRunItem(item)" @click="executarItem(item.id)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-play</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Executar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDeleteItem(item)" @click="openDialogDelete(item.id, item.nome)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Deletar</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="canDownloadLogs(item) && userIsAdmin"
                :disabled="loadingLogsGerais"
                @click="downloadLogsGerais(item.id)"
              >
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon v-if="!loadingLogsGerais" small>mdi-file-download-outline</v-icon>
                  <v-progress-circular v-else indeterminate color="gray" size="17" />
                </v-list-item-icon>
                <v-list-item-title>Download Logs Gerais</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDownloadLogs(item)" :disabled="loadingLogsUsuario" @click="downloadLogsUsuario(item.id)">
                <v-list-item-icon class="mx-0 my-0 py-4">
                  <v-icon v-if="!loadingLogsUsuario" small>mdi-file-download-outline</v-icon>
                  <v-progress-circular v-else indeterminate color="gray" size="17" />
                </v-list-item-icon>
                <v-list-item-title>Download Logs Processamento</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template v-slot:[`footer.page-text`]>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" @click="$emit('rechargeTable')">mdi-refresh</v-icon>
            </template>
            <span>Clique aqui para recarregar os registros</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </base-material-card>
    <dialog-delete
      :show-dialog="dialogDelete"
      @changeValue="dialogDelete = $event"
      @deleteTriggered="deletarItem(dialogDeleteData.id)"
    >
      <template slot="title">
        <span style="word-break: break-word">Deletar Cálculo Bulldozerr #{{ dialogDeleteData.id }}</span>
      </template>
      <template slot="body">
        Tem certeza que deseja deletar o cálculo bulldozerr
        <strong>{{ dialogDeleteData.titulo }}</strong>
        ?
      </template>
    </dialog-delete>
    <dialog-detalhes
      ref="dialogDetalhes"
      persistent
      title="Detalhes da Conciliação"
      :show-dialog="dialogDetalhes"
      @changeValue="dialogDetalhes = $event"
    >
      <template slot="body">
        <v-data-table
          class="elevation-1 style-table-dialog-detalhes"
          disable-pagination
          hide-default-footer
          :headers="dialogDetalhesHeader"
          :items="dialogDetalhesData"
          :mobile-breakpoint="10"
        />
      </template>
    </dialog-detalhes>
    <dialog-download :id="idDownload" :etapas="etapas" :dialog.sync="dialogDownload" @download="downloadItem" />
  </v-container>
</template>

<script>
  import powerupsActionsMixins from '@/mixins/powerupsActionsMixins.js';
  import BulldozerrService from '@/services/BulldozerrService';

  export default {
    components: {
      DialogDelete: () => import('@/components/general/DialogDelete.vue'),
      GeneralStatusStep: () => import('@/components/general/GeneralStatusStep.vue'),
      DialogDetalhes: () => import('@/components/general/GeneralDialogDetalhes.vue'),
      DialogDownload: () => import('@/components/bulldozerr/DialogDownload.vue'),
      GeneralProgressBars: () => import('@/components/general/GeneralProgressBars.vue')
    },
    mixins: [powerupsActionsMixins],

    props: {
      items: {
        type: Array,
        default: () => []
      },
      loading: {
        type: Boolean,
        default: false
      }
    },

    emits: ['rechargeTable'],

    data: () => ({
      powerupService: BulldozerrService,
      powerupEntityName: 'Bulldozerr',
      search: '',
      dialogDownload: false,
      headers: [
        {
          text: '#',
          value: 'id'
        },
        {
          text: 'Nome',
          value: 'nome',
          align: 'center'
        },
        {
          text: 'Usuário',
          value: 'usuario.nome',
          align: 'center'
        },
        {
          text: 'Executado',
          value: 'data_processamento',
          align: 'center'
        },
        {
          text: 'Status',
          value: 'status',
          align: 'center',
          sortable: false
        },
        {
          sortable: false,
          text: 'Ações',
          value: 'actions',
          class: 'pl-4',
          align: 'center'
        }
      ],
      idDownload: null,
      etapas: [],
      countFunction: 0,
      loadingLogsGerais: false,
      loadingLogsUsuario: false
    }),

    computed: {
      userIsAdmin() {
        return this.$store.getters.userIsAdmin;
      }
    },

    methods: {
      openDialogDelete(id, titulo) {
        this.dialogDeleteData = { id, titulo };
        this.dialogDelete = true;
      },

      downloadItem(item) {
        const id = item.id;
        const nomeCompletoArquivo = `Resultado Bulldozerr-${id} Etapa ${item.type}.zip`;

        this.$set(this.downloadProgressData, id, { id });
        this.$toast.info('Preparando seu arquivo para download.', '', {
          position: 'topRight',
          timeout: 2000
        });

        BulldozerrService.download(id, item.type, {
          timeout: 60 * 60 * 1000,
          onDownloadProgress: (progressEvent) => {
            let progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            let isComputable = progressEvent.lengthComputable;

            this.$set(this.downloadProgressData, id, {
              progress,
              isComputable,
              id
            });
          }
        })
          .then(async (res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeCompletoArquivo);
            document.body.appendChild(link);
            link.click();
          })
          .catch((err) => {
            this.$toast.error('Falha no download do resultado.', '', {
              position: 'topRight'
            });
            console.error('Error: ', err);
          })
          .finally(() => {
            this.$delete(this.downloadProgressData, id);
          });
      },

      canDownload(item) {
        return (
          item.status_etapa_1 === 'SUCESSO' ||
          item.status_etapa_2 === 'SUCESSO' ||
          item.status_etapa_3 === 'SUCESSO' ||
          item.status_etapa_4 === 'SUCESSO' ||
          item.status_etapa_5 === 'SUCESSO' ||
          item.status_etapa_6 === 'SUCESSO' ||
          item.status_etapa_7 === 'SUCESSO'
        );
      },

      openDialogDownload(item) {
        this.etapas = [
          {
            nome: 'Análise ICMS',
            status: item.status_etapa_3,
            tipo: 'icms'
          },
          {
            nome: 'Analise Banco de Compras',
            status: item.status_etapa_4,
            tipo: 'banco_compras'
          },
          {
            nome: 'BDGD',
            status: item.status_etapa_2,
            tipo: 'bdgd'
          },
          {
            nome: 'AIS',
            status: item.status_etapa_1,
            tipo: 'ais'
          },
          {
            nome: 'Análise da Base Incremental',
            status: item.status_etapa_5,
            tipo: 'base_incremental'
          },
          {
            nome: 'Análise das Obrigações Especiais',
            status: item.status_etapa_6,
            tipo: 'obrigacoes_especiais'
          },
          {
            nome: 'Análise da Base Blindada',
            status: item.status_etapa_7,
            tipo: 'base_blindada'
          }
        ];

        this.idDownload = item.id;
        this.dialogDownload = true;
      },

      canRunItem(item) {
        return (
          item.status_etapa_1 !== 'EXECUTANDO' &&
          item.status_etapa_2 !== 'EXECUTANDO' &&
          item.status_etapa_3 !== 'EXECUTANDO' &&
          item.status_etapa_4 !== 'EXECUTANDO' &&
          item.status_etapa_5 !== 'EXECUTANDO' &&
          item.status_etapa_6 !== 'EXECUTANDO' &&
          item.status_etapa_7 !== 'EXECUTANDO'
        );
      },

      canDownloadLogs(item) {
        return (
          item.status_etapa_1 !== 'AGUARDANDO' ||
          item.status_etapa_2 !== 'AGUARDANDO' ||
          item.status_etapa_3 !== 'AGUARDANDO' ||
          item.status_etapa_4 !== 'AGUARDANDO' ||
          item.status_etapa_5 !== 'AGUARDANDO' ||
          item.status_etapa_6 !== 'AGUARDANDO' ||
          item.status_etapa_7 !== 'AGUARDANDO'
        );
      },

      downloadLogsGerais(id) {
        this.$toast.info('Preparando seu arquivo de logs para download.', '', {
          position: 'topRight',
          timeout: 2000
        });

        if (this.loadingLogsGerais) return false;

        this.loadingLogsGerais = true;

        BulldozerrService.baixarLogs(id, 'geral')
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `logs-gerais-${id}.log`);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            this.$toast.error('Falha no download dos logs gerais.', '', {
              position: 'topRight'
            });
          })
          .finally(() => (this.loadingLogsGerais = false));
      },

      downloadLogsUsuario(id) {
        this.$toast.info('Preparando seu arquivo de logs para download.', '', {
          position: 'topRight',
          timeout: 2000
        });

        if (this.loadingLogsUsuario) return false;

        this.loadingLogsUsuario = true;

        BulldozerrService.baixarLogs(id, 'user')
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `logs-processamento-${id}.log`);
            document.body.appendChild(link);
            link.click();
          })
          .catch(() => {
            this.$toast.error('Falha no download dos logs de processamento.', '', { position: 'topRight' });
          })
          .finally(() => (this.loadingLogsUsuario = false));
      }
    }
  };
</script>
